import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ReportSetting } from 'src/app/shared/interface/company.interface';
import { FileType } from '../../file-viewer/interface/file-viewer.interface';
import { ExtendedPropertyDetail } from '../../property/interface/property.interface';
import { OrderDetail, PreviewSnapShotKeys, SpecificComparable, ValuationType } from '../interface/order.interface';

@Injectable({
  providedIn: 'root',
})
export class OrderState {
  /** Stores details related to a single active order */
  private _orderDetailSub$ = new BehaviorSubject<OrderDetail>({} as OrderDetail);
  orderDetail$ = this._orderDetailSub$.asObservable();

  get orderDetail(): OrderDetail {
    return this._orderDetailSub$.value;
  }

  set orderDetail(order: OrderDetail) {
    this._orderDetailSub$.next(order);
  }

  /** Get active order's report type */
  get reportType(): string {
    return this.orderDetail?.reportType ?? '';
  }

  /** Stores an order specific setting */
  private _orderSettingSub$ = new BehaviorSubject<ReportSetting | undefined>(undefined);
  orderSetting$ = this._orderSettingSub$.asObservable();

  get orderSetting(): ReportSetting | undefined {
    return this._orderSettingSub$.value;
  }

  set orderSetting(value: ReportSetting | undefined) {
    this._orderSettingSub$.next(value);
  }

  /** Stores the active valuation type, i.e. Sales, Cost, STR or LTR based on the current page that the user is in. */
  private _activeValuationTypeSub$ = new BehaviorSubject<ValuationType | null>(null);
  activeValuationType$ = this._activeValuationTypeSub$.asObservable();

  get activeValuationType(): ValuationType | null {
    return this._activeValuationTypeSub$.value;
  }

  set activeValuationType(valuationType: ValuationType | null) {
    this._activeValuationTypeSub$.next(valuationType);
  }

  /** Stores form data of a specific comp while adding it to the order. */
  private _specificComparableSub$ = new BehaviorSubject<SpecificComparable | null>(null);
  specificComparable$ = this._specificComparableSub$.asObservable();

  get specificComparable(): SpecificComparable | null {
    return this._specificComparableSub$.value;
  }

  set specificComparable(value: SpecificComparable | null) {
    this._specificComparableSub$.next(value);
  }

  /** Stores inspection record fetch status */
  private _inspectionFetchStatusSub$ = new Subject<boolean>();
  inspectionFetchStatus$ = this._inspectionFetchStatusSub$.asObservable();

  set inspectionFetchStatus(status: boolean) {
    this._inspectionFetchStatusSub$.next(status);
  }

  /**
   * @private Not to be used independently
   * Stores all selected comps across all approaches under all scenarios.
   * The dictionary has a key that is a combination of scenarioId+valuationType+propertyId
   */
  public propertyStore = new Map<string, ExtendedPropertyDetail>();

  /** @private Not to be used independently */
  public selectedComparableStoreByScenario = new Map<string, Set<string>>();

  /** @private Not to be used independently */
  public selectedComparableStoreByValuationType = new Map<string, Set<ValuationType>>();

  /** @private Not to be used independently */
  public selectedComparableStoreByScenarioValuationType = new Map<string, Set<string>>();

  /** Stores the cached Preview HTML to be reused if no changes are made to the order. */
  public cachedPreviewHtml?: Blob;

  /** Stores the previous state of various states to compare between their current state */
  public previewSnapshots?: { [key in PreviewSnapShotKeys]?: string };

  /** Stores the cached additional docs for reuse. */
  public cachedAdditionalDocs?: Array<{
    file: Blob | string;
    fileType: FileType;
    label: string;
    createdDate: Date;
  }>;

  /** Stores if the order is in read-only mode */
  private _readOnlyModeSub$ = new BehaviorSubject<boolean>(false);
  readOnlyMode$ = this._readOnlyModeSub$.asObservable();

  get readOnlyMode(): boolean {
    return this._readOnlyModeSub$.value;
  }

  set readOnlyMode(value: boolean) {
    this._readOnlyModeSub$.next(value);
  }

  /** Stores if read-only mode was forced by another session holding the edit lock */
  private _lockReadOnlySub$ = new BehaviorSubject<boolean>(false);
  lockReadOnly$ = this._lockReadOnlySub$.asObservable();

  get lockReadOnly(): boolean {
    return this._lockReadOnlySub$.value;
  }

  set lockReadOnly(value: boolean) {
    this._lockReadOnlySub$.next(value);
  }
}
